import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import Seo from './Seo';
import { getPost } from '../blog/posts';

const SITE_URL = 'https://qcoda.com';

function NotFound() {
    return (
        <main>
            <Seo
                title="Post not found — Qcoda Blog"
                description="The blog post you are looking for could not be found."
                path="/blog"
                noindex
            />
            <Container className="text-white py-5" style={{ minHeight: '70vh' }}>
                <h1 className="fw-bold mb-3">Post not found</h1>
                <p className="text-dim">That essay doesn't exist (yet).</p>
                <Link to="/blog" className="blog-back-link">← Back to the blog</Link>
            </Container>
        </main>
    );
}

// Single post page. Self-referential canonical (/blog/:slug), BlogPosting +
// BreadcrumbList JSON-LD, and AEO-friendly semantics: one <h1>, a visible dek,
// a real <time>, an explicit byline, and clean <h2> section structure so
// answer engines can lift and attribute passages cleanly.
function BlogPost() {
    const { slug } = useParams();
    const post = getPost(slug);

    if (!post) return <NotFound />;

    // bodyHtml is an authored HTML string from src/blog/bodies/<slug>.js.
    // dangerouslySetInnerHTML is safe — this is never user-supplied input.
    const url = `${SITE_URL}/blog/${post.slug}`;
    const image = post.heroImage ? `${SITE_URL}${post.heroImage}` : `${SITE_URL}/qcoda_og.png`;

    const articleJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        alternativeHeadline: post.subtitle,
        description: post.description,
        image,
        datePublished: post.date,
        dateModified: post.date,
        inLanguage: 'en-US',
        author: {
            '@type': 'Person',
            name: post.author,
            jobTitle: post.authorTitle,
            sameAs: 'https://www.linkedin.com/in/aschwabe/',
        },
        publisher: {
            '@type': 'Organization',
            name: 'Qcoda',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/qcoda_icon_black.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url,
    };

    const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
            { '@type': 'ListItem', position: 3, name: post.title, item: url },
        ],
    };

    return (
        <main>
            <Seo
                title={`${post.title} — Qcoda Blog`}
                description={post.description}
                path={`/blog/${post.slug}`}
                image={post.heroImage}
                type="article"
                author={post.author}
                publishedTime={post.date}
                jsonLd={[articleJsonLd, breadcrumbJsonLd]}
                jsonLdId="post-jsonld"
            />

            <article className="site-section-lg">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            {/* Visible breadcrumb mirrors the JSON-LD trail. */}
                            <nav aria-label="Breadcrumb" className="blog-breadcrumb">
                                <Link to="/">Home</Link>
                                <span aria-hidden="true"> / </span>
                                <Link to="/blog">Blog</Link>
                                <span aria-hidden="true"> / </span>
                                <span className="blog-breadcrumb-current">{post.title}</span>
                            </nav>

                            <header className="blog-post-header">
                                <h1 className="blog-post-title">{post.title}</h1>
                                <p className="blog-post-dek">{post.subtitle}</p>
                                <div className="blog-post-byline">
                                    <span className="blog-post-author">{post.author}</span>
                                    {post.authorTitle && (
                                        <span className="blog-post-author-title">, {post.authorTitle}</span>
                                    )}
                                    <span aria-hidden="true"> · </span>
                                    <time dateTime={post.date}>{post.dateDisplay}</time>
                                    <span aria-hidden="true"> · </span>
                                    <span>{post.readingTime}</span>
                                </div>
                            </header>

                            {post.heroImage && (
                                <figure className="blog-post-hero">
                                    <img src={post.heroImage} alt={post.heroAlt || post.title} />
                                </figure>
                            )}

                            <div
                                className="blog-post-body"
                                dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
                            />

                            <footer className="blog-post-footer">
                                <Link to="/blog" className="blog-back-link">← More from the blog</Link>
                                <a href="/#waitlist" className="btn-primary-cta blog-post-cta">
                                    Ask about early access
                                </a>
                            </footer>
                        </Col>
                    </Row>
                </Container>
            </article>
        </main>
    );
}

export default BlogPost;
